(() => {
  "use strict";

  const doc = document;
  const body = doc.body;
  const currency = window.Shopify?.currency?.active || "CAD";
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || "en-CA", { style: "currency", currency }).format((Number(cents) || 0) / 100);
  const focusable = "a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex='-1'])";
  let activeDialog = null;
  let returnFocus = null;

  function openDialog(panel, scrim, trigger) {
    if (!panel) return;
    returnFocus = trigger || doc.activeElement;
    activeDialog = panel;
    panel.hidden = false;
    panel.removeAttribute("inert");
    if (scrim) scrim.hidden = false;
    requestAnimationFrame(() => {
      panel.classList.add("is-open");
      scrim?.classList.add("is-open");
    });
    panel.setAttribute("aria-hidden", "false");
    body.classList.add("dialog-open");
    panel.querySelector(focusable)?.focus();
  }

  function closeDialog(panel, scrim) {
    if (!panel) return;
    panel.classList.remove("is-open");
    scrim?.classList.remove("is-open");
    panel.setAttribute("aria-hidden", "true");
    body.classList.remove("dialog-open");
    window.setTimeout(() => {
      panel.hidden = panel.matches(".search-overlay") ? true : false;
      panel.setAttribute("inert", "");
      if (scrim) scrim.hidden = true;
    }, 260);
    if (panel.matches("[data-mobile-menu]")) doc.querySelector("[data-menu-open]")?.setAttribute("aria-expanded", "false");
    activeDialog = null;
    if (returnFocus instanceof HTMLElement) returnFocus.focus();
  }

  doc.addEventListener("keydown", (event) => {
    if (!activeDialog) return;
    if (event.key === "Escape") {
      const type = activeDialog.matches("[data-mobile-menu]") ? "menu" : activeDialog.matches("[data-cart-drawer]") ? "cart" : "search";
      closeDialog(activeDialog, doc.querySelector(`[data-${type}-scrim]`));
      return;
    }
    if (event.key !== "Tab") return;
    const items = Array.from(activeDialog.querySelectorAll(focusable)).filter((node) => node.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  function initHeader(scope = doc) {
    const header = doc.querySelector("[data-sticky-header]");
    const menu = doc.querySelector("[data-mobile-menu]");
    const menuScrim = doc.querySelector("[data-menu-scrim]");
    scope.querySelectorAll("[data-menu-open]").forEach((button) => button.addEventListener("click", () => { openDialog(menu, menuScrim, button); button.setAttribute("aria-expanded", "true"); }));
    scope.querySelectorAll("[data-menu-close]").forEach((button) => button.addEventListener("click", () => { closeDialog(menu, menuScrim); doc.querySelector("[data-menu-open]")?.setAttribute("aria-expanded", "false"); }));
    menuScrim?.addEventListener("click", () => closeDialog(menu, menuScrim));
    menu?.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => closeDialog(menu, menuScrim)));

    const search = doc.querySelector("[data-search-overlay]");
    scope.querySelectorAll("[data-search-open]").forEach((button) => button.addEventListener("click", () => openDialog(search, null, button)));
    scope.querySelectorAll("[data-search-close]").forEach((button) => button.addEventListener("click", () => closeDialog(search, null)));

    const update = () => header?.classList.toggle("is-scrolled", window.scrollY > 18);
    update();
    window.addEventListener("scroll", update, { passive: true });
  }

  function initPredictiveSearch(scope = doc) {
    scope.querySelectorAll("[data-predictive-search]").forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = "true";
      const input = form.querySelector("input[type='search']");
      const output = form.querySelector("[data-predictive-results]");
      let timer;
      let controller;
      input?.addEventListener("input", () => {
        window.clearTimeout(timer);
        const query = input.value.trim();
        if (query.length < 2) { output.replaceChildren(); input.setAttribute("aria-expanded", "false"); return; }
        timer = window.setTimeout(async () => {
          controller?.abort();
          controller = new AbortController();
          try {
            const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article,collection&resources[limit]=6&resources[options][unavailable_products]=last`;
            const response = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } });
            const data = await response.json();
            const groups = data.resources?.results || {};
            output.replaceChildren();
            const results = ["products", "collections", "pages", "articles"].flatMap((type) => (groups[type] || []).map((item) => ({ ...item, type })));
            results.forEach((item) => {
              const link = doc.createElement("a");
              const emergencyItem = item.handle === "emergency-water-100-year-shelf-life";
              const duplicateFlavour = item.type === "products" && Number(item.price || 0) === 0 && /sparkling|infused/i.test(`${item.title || ""} ${item.product_type || ""}`);
              link.href = duplicateFlavour ? (window.ywcRoutes?.miraclesProduct || item.url) : item.url;
              link.className = "predictive-item";
              link.setAttribute("role", "option");
              if (item.image) { const image = doc.createElement("img"); image.src = item.image; image.alt = ""; image.width = 56; image.height = 56; link.append(image); }
              const copy = doc.createElement("span");
              const label = doc.createElement("small"); label.textContent = item.type.slice(0, -1);
              const title = doc.createElement("strong"); title.textContent = emergencyItem ? "Emergency Canned Water" : item.title;
              copy.append(label, title); link.append(copy); output.append(link);
            });
            if (!results.length) { const empty = doc.createElement("p"); empty.textContent = "No quick results. Press Search for a full search."; output.append(empty); }
            input.setAttribute("aria-expanded", results.length ? "true" : "false");
          } catch (error) { if (error.name !== "AbortError") output.replaceChildren(); }
        }, 180);
      });
    });
  }

  function initReveal(scope = doc) {
    const elements = scope.querySelectorAll("[data-reveal]:not(.is-visible)");
    if (matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) { elements.forEach((el) => el.classList.add("is-visible")); return; }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add("is-visible"); observer.unobserve(entry.target); } }), { threshold: 0.08, rootMargin: "0px 0px -30px" });
    elements.forEach((el) => observer.observe(el));
  }

  function initQuantity(scope = doc) {
    scope.querySelectorAll("[data-quantity-control]").forEach((control) => {
      if (control.dataset.ready) return;
      control.dataset.ready = "true";
      const input = control.querySelector("[data-quantity]");
      const step = () => Number(input?.step || 1);
      control.querySelector("[data-quantity-minus]")?.addEventListener("click", () => { input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) - step())); input.dispatchEvent(new Event("change", { bubbles: true })); });
      control.querySelector("[data-quantity-plus]")?.addEventListener("click", () => { const max = input.max ? Number(input.max) : Infinity; input.value = String(Math.min(max, Number(input.value || 1) + step())); input.dispatchEvent(new Event("change", { bubbles: true })); });
    });
  }

  function showMedia(root, id) {
    if (!id) return;
    const items = root.querySelectorAll("[data-media-id]");
    let index = 0;
    items.forEach((item, i) => {
      const active = String(item.dataset.mediaId) === String(id);
      item.hidden = !active;
      item.classList.toggle("is-active", active);
      if (active) index = i + 1;
      if (!active) item.querySelectorAll("video").forEach((video) => video.pause());
    });
    root.querySelectorAll("[data-media-thumb]").forEach((thumb) => { const active = String(thumb.dataset.mediaThumb) === String(id); thumb.classList.toggle("is-active", active); thumb.setAttribute("aria-current", String(active)); });
    const counter = root.querySelector("[data-gallery-index]"); if (counter) counter.textContent = String(index);
  }

  function initProducts(scope = doc) {
    scope.querySelectorAll("[data-product-root]").forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = "true";
      let data;
      try { data = JSON.parse(root.querySelector("[data-product-json]")?.textContent || "{}"); } catch (_) { return; }
      const form = root.querySelector("[data-product-form]");
      const idInput = form?.querySelector("[data-variant-id]");
      const optionInputs = Array.from(form?.querySelectorAll("[data-option-value]") || []);
      const price = root.querySelector("[data-product-price]");
      const compare = root.querySelector("[data-compare-price]");
      const add = root.querySelector("[data-add-button]");
      const available = root.querySelector("[data-availability]");
      const flavourMedia = Array.from(root.querySelectorAll("[data-flavour-media]"));
      let flavourContent = {};
      let variantFacts = {};
      try { flavourContent = JSON.parse(root.querySelector("[data-flavour-content-json]")?.textContent || "{}"); } catch (_) { flavourContent = {}; }
      try { variantFacts = JSON.parse(root.querySelector("[data-variant-facts-json]")?.textContent || "{}"); } catch (_) { variantFacts = {}; }
      const flavourKey = (value = "") => {
        const normalized = String(value).trim().toLowerCase();
        if (!normalized) return "";
        if (normalized.includes("rose")) return "rose";
        if (normalized.includes("mint")) return "mint";
        if (normalized.includes("cinnamon")) return "cinnamon";
        if (normalized.includes("pollen")) return "pollen";
        if (normalized.includes("thyme")) return "thyme";
        if (normalized.includes("oliban") || normalized.includes("frankincense")) return "oliban";
        if (normalized.includes("sparkling") || normalized.includes("serenity") || normalized.includes("classic")) return "sparkling";
        if (normalized.includes("variety") || normalized.includes("discovery")) return "variety";
        return "";
      };

      function selectedFlavourKey(variant) {
        const explicitInput = form?.querySelector("[data-flavour-key]:checked");
        if (explicitInput?.dataset.flavourKey) return explicitInput.dataset.flavourKey;

        const selectedValues = Array.from(form?.querySelectorAll("[data-option-value]:checked") || [])
          .map((input) => input.value);
        for (const value of selectedValues) {
          const key = flavourKey(value);
          if (key) return key;
        }

        const variantValues = Array.isArray(variant?.options)
          ? variant.options
          : [variant?.option1, variant?.option2, variant?.option3];
        for (const value of variantValues) {
          const key = flavourKey(value);
          if (key) return key;
        }

        return flavourKey(variant?.title) || root.dataset.initialFlavourKey || "variety";
      }

      function showFlavourMedia(key) {
        flavourMedia.forEach((item) => {
          const active = item.dataset.flavourMedia === key;
          item.hidden = !active;
          item.classList.toggle("is-active", active);
        });
      }

      function updateFlavourContent(key) {
        const content = flavourContent[key] || flavourContent.variety;
        if (!content) return;
        root.querySelectorAll("[data-flavour-name]").forEach((node) => { node.textContent = content.name; });
        root.querySelectorAll("[data-flavour-intro]").forEach((node) => { node.textContent = content.intro; });
        root.querySelectorAll("[data-flavour-profile]").forEach((node) => { node.textContent = content.profile; });
        root.querySelectorAll("[data-flavour-formula]").forEach((node) => { node.textContent = content.formula; });
      }

      function chooseVariant() {
        const positions = Array.from(form?.querySelectorAll("[data-option-position]") || []);
        const selected = positions.map((fieldset) => fieldset.querySelector("[data-option-value]:checked")?.value);
        positions.forEach((fieldset) => { const label = fieldset.querySelector("[data-selected-option]"); if (label) label.textContent = fieldset.querySelector("[data-option-value]:checked")?.value || ""; });
        const variant = (data.variants || []).find((item) => item.options.every((value, index) => value === selected[index])) || (data.variants || [])[0];
        if (!variant) return;
        if (idInput) idInput.value = variant.id;
        if (price) price.textContent = `${money(variant.price)}${window.Shopify?.currency?.active ? ` ${currency}` : ""}`;
        if (compare) { compare.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : ""; compare.hidden = !(variant.compare_at_price > variant.price); }
        if (add) { add.disabled = !variant.available; add.textContent = variant.available ? add.dataset.availableText : add.dataset.soldOutText; }
        if (available) { available.innerHTML = variant.available ? "<i></i> Available to order" : "Currently unavailable"; }
        const facts = variantFacts[String(variant.id)] || {};
        const packValue = root.querySelector("[data-pack-value]");
        const packDetail = root.querySelector("[data-pack-detail]");
        const shippingValue = root.querySelector("[data-shipping-value]");
        const selectedVariant = root.querySelector("[data-selected-variant]");
        if (packValue && facts.pack) packValue.textContent = facts.pack;
        if (packDetail && facts.detail) packDetail.textContent = facts.detail;
        if (shippingValue && facts.shipping) shippingValue.textContent = facts.shipping;
        if (selectedVariant) selectedVariant.textContent = facts.title || variant.title;
        const mediaId = variant.featured_media?.id || variant.featured_image?.id;
        const activeFlavourKey = selectedFlavourKey(variant);
        if (flavourMedia.length) showFlavourMedia(activeFlavourKey);
        else if (mediaId) showMedia(root, mediaId);
        updateFlavourContent(activeFlavourKey);
        const url = new URL(window.location.href); url.searchParams.set("variant", variant.id); history.replaceState({}, "", url);
      }
      optionInputs.forEach((input) => {
        input.addEventListener("change", chooseVariant);
        input.addEventListener("input", chooseVariant);
      });
      form?.addEventListener("click", (event) => {
        if (!event.target.closest(".option-chip")) return;
        window.setTimeout(chooseVariant, 0);
      });
      root.querySelectorAll("[data-media-thumb]").forEach((thumb) => thumb.addEventListener("click", () => showMedia(root, thumb.dataset.mediaThumb)));
      chooseVariant();
    });
  }

  async function getCart() { const response = await fetch("/cart.js", { headers: { Accept: "application/json" } }); return response.json(); }
  function renderCart(cart) {
    doc.querySelectorAll("[data-cart-count]").forEach((node) => node.textContent = cart.item_count);
    const output = doc.querySelector("[data-cart-items]");
    const footer = doc.querySelector("[data-cart-footer]");
    if (!output) return;
    output.replaceChildren();
    if (!cart.items.length) {
      const empty = doc.createElement("div"); empty.className = "drawer-empty";
      const p = doc.createElement("p"); p.textContent = "Your cart is empty.";
      const a = doc.createElement("a"); a.href = window.ywcRoutes?.shop || "/collections/all"; a.className = "button button-dark"; a.textContent = "Explore both ranges";
      empty.append(p, a); output.append(empty); if (footer) footer.hidden = true; return;
    }
    cart.items.forEach((item) => {
      const article = doc.createElement("article"); article.className = "drawer-line";
      const media = doc.createElement("a"); media.href = item.url;
      const displayTitle = item.handle === "emergency-water-100-year-shelf-life" ? "Emergency Canned Water" : item.product_title;
      if (item.image) { const img = doc.createElement("img"); img.src = `${item.image}${item.image.includes("?") ? "&" : "?"}width=180`; img.alt = displayTitle; img.width = 90; img.height = 90; media.append(img); }
      const info = doc.createElement("div");
      const title = doc.createElement("a"); title.href = item.url; const strong = doc.createElement("strong"); strong.textContent = displayTitle; title.append(strong); info.append(title);
      if (item.variant_title) { const small = doc.createElement("small"); small.textContent = item.variant_title; info.append(small); }
      const value = doc.createElement("span"); value.textContent = `${item.quantity} × ${money(item.final_price)}`; info.append(value);
      const remove = doc.createElement("button"); remove.type = "button"; remove.className = "drawer-remove"; remove.textContent = "Remove"; remove.addEventListener("click", async () => { await fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ id: item.key, quantity: 0 }) }); renderCart(await getCart()); });
      article.append(media, info, remove); output.append(article);
    });
    if (footer) footer.hidden = false;
    const total = doc.querySelector("[data-cart-total]"); if (total) total.textContent = money(cart.total_price);
  }

  async function addForm(form, button) {
    const original = button?.textContent;
    if (button) { button.disabled = true; button.textContent = "Adding…"; }
    try {
      const response = await fetch("/cart/add.js", { method: "POST", headers: { Accept: "application/json" }, body: new FormData(form) });
      if (!response.ok) throw new Error((await response.json()).description || "Could not add this item.");
      const cart = await getCart(); renderCart(cart);
      const drawer = doc.querySelector("[data-cart-drawer]"); const scrim = doc.querySelector("[data-cart-scrim]");
      if (drawer) openDialog(drawer, scrim, button); else window.location.assign("/cart");
    } catch (error) { window.alert(error.message); }
    finally { if (button) { button.disabled = false; button.textContent = original; } }
  }

  function initCart(scope = doc) {
    const drawer = doc.querySelector("[data-cart-drawer]");
    const scrim = doc.querySelector("[data-cart-scrim]");
    scope.querySelectorAll("[data-cart-open]").forEach((trigger) => trigger.addEventListener("click", (event) => { if (!drawer) return; event.preventDefault(); openDialog(drawer, scrim, trigger); }));
    scope.querySelectorAll("[data-cart-close]").forEach((button) => button.addEventListener("click", () => closeDialog(drawer, scrim)));
    scrim?.addEventListener("click", () => closeDialog(drawer, scrim));
    scope.querySelectorAll("form[data-product-form],form[data-quick-add]").forEach((form) => { if (form.dataset.cartReady) return; form.dataset.cartReady = "true"; form.addEventListener("submit", (event) => { event.preventDefault(); addForm(form, event.submitter || form.querySelector("button[type='submit']")); }); });
  }

  function initCalculator(scope = doc) {
    scope.querySelectorAll("[data-calculator]").forEach((calculator) => {
      if (calculator.dataset.ready) return; calculator.dataset.ready = "true";
      const people = calculator.querySelector("[name='people']"); const days = calculator.querySelector("[name='days']"); const uses = calculator.querySelectorAll("[name='water-use']");
      const update = () => {
        const count = Math.max(1, Number(people.value || 1)); const duration = Math.max(1, Number(days.value || 1)); const daily = Number(Array.from(uses).find((node) => node.checked)?.value || 2); const litres = count * duration * daily; const cans = Math.ceil(litres / 0.355); const cases = Math.ceil(cans / 12);
        calculator.querySelector("[data-result-cans]").textContent = cans.toLocaleString(); calculator.querySelector("[data-result-cases]").textContent = `${cases.toLocaleString()} × 12-can case${cases === 1 ? "" : "s"}`; calculator.querySelector("[data-result-litres]").textContent = `${litres.toLocaleString()} L`; calculator.querySelector("[data-result-daily]").textContent = `${daily} L / person / day`; calculator.querySelector("[data-result-recommendation]").textContent = cases < 3 ? "Start with a compact reserve" : cases < 20 ? "Build a household reserve" : "Plan a larger supply";
      };
      [people, days, ...uses].forEach((node) => node.addEventListener("input", update)); update();
    });
  }

  function initFlavours(scope = doc) {
    scope.querySelectorAll("[data-flavour-explorer]").forEach((root) => {
      const display = root.querySelector("[data-flavour-display]");
      const image = root.querySelector("[data-flavour-image]");
      const title = root.querySelector("[data-flavour-title]");
      const note = root.querySelector("[data-flavour-note]");
      const tabs = Array.from(root.querySelectorAll("[data-flavour-tab]"));
      const activate = (tab, focus = false) => {
        tabs.forEach((other) => {
          const active = other === tab;
          other.classList.toggle("is-active", active);
          other.setAttribute("aria-selected", String(active));
          other.tabIndex = active ? 0 : -1;
        });
        display?.style.setProperty("--active-flavour", tab.dataset.colour);
        image?.classList.add("is-changing");
        window.setTimeout(() => {
          if (image) { image.src = tab.dataset.image; image.alt = `Miracles of Nature ${tab.dataset.title}`; image.classList.remove("is-changing"); }
          if (title) title.textContent = tab.dataset.title;
          if (note) note.textContent = tab.dataset.note;
        }, 140);
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.tabIndex = tab.classList.contains("is-active") ? 0 : -1;
        tab.addEventListener("click", () => activate(tab));
        tab.addEventListener("keydown", (event) => {
          let next = null;
          if (event.key === "ArrowRight" || event.key === "ArrowDown") next = tabs[(index + 1) % tabs.length];
          if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === "Home") next = tabs[0];
          if (event.key === "End") next = tabs[tabs.length - 1];
          if (next) { event.preventDefault(); activate(next, true); }
        });
      });
    });
  }

  function initVideo(scope = doc) {
    scope.querySelectorAll("[data-video-frame]").forEach((frame) => {
      const video = frame.querySelector("video"); const button = frame.querySelector("[data-video-toggle]"); if (!video || !button) return;
      const update = () => { const playing = !video.paused; frame.classList.toggle("is-playing", playing); button.setAttribute("aria-label", playing ? "Pause video" : "Play video"); button.querySelector("span").textContent = playing ? "Pause film" : "Play film"; };
      button.addEventListener("click", () => video.paused ? video.play() : video.pause()); video.addEventListener("play", update); video.addEventListener("pause", update); update();
    });
  }

  function initFilters(scope = doc) {
    const panel = doc.querySelector("[data-filter-panel]");
    scope.querySelectorAll("[data-filter-open]").forEach((button) => button.addEventListener("click", () => panel?.classList.add("is-open")));
    scope.querySelectorAll("[data-filter-close]").forEach((button) => button.addEventListener("click", () => panel?.classList.remove("is-open")));
    scope.querySelectorAll("[data-sort-select]").forEach((select) => select.addEventListener("change", () => select.form?.submit()));
  }

  function initRecipes(scope = doc) {
    const openHashedRecipe = () => {
      if (!window.location.hash) return;
      const recipe = doc.querySelector(window.location.hash);
      if (!recipe?.matches("[data-recipe]")) return;
      recipe.hidden = false;
      const details = recipe.querySelector("details");
      if (details) details.open = true;
      window.setTimeout(() => recipe.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    };

    scope.querySelectorAll("[data-recipe-library]").forEach((root) => {
      root.querySelectorAll("[data-recipe-filter]").forEach((button) => {
        button.addEventListener("click", () => {
          const value = button.dataset.recipeFilter;
          root.querySelectorAll("[data-recipe-filter]").forEach((other) => {
            other.classList.toggle("is-active", other === button);
          });
          root.querySelectorAll("[data-recipe]").forEach((card) => {
            card.hidden = value !== "all" && card.dataset.recipe !== value;
          });
        });
      });
    });

    openHashedRecipe();
    if (!window.ywcRecipeHashListener) {
      window.ywcRecipeHashListener = true;
      window.addEventListener("hashchange", openHashedRecipe);
    }
  }

  function initRecommendations(scope = doc) {
    scope.querySelectorAll("[data-recommendations-url]").forEach(async (section) => { if (section.dataset.loaded || section.innerHTML.trim()) return; section.dataset.loaded = "true"; try { const response = await fetch(section.dataset.recommendationsUrl); const html = new DOMParser().parseFromString(await response.text(), "text/html"); const content = html.querySelector("[data-recommendations-url]"); if (content?.innerHTML.trim()) section.innerHTML = content.innerHTML; } catch (_) {} });
  }

  function initMisc(scope = doc) {
    scope.querySelectorAll("[data-copy-link]").forEach((button) => button.addEventListener("click", async () => { await navigator.clipboard.writeText(location.href); button.textContent = "Copied"; window.setTimeout(() => button.textContent = button.dataset.copyText || "Copy link", 1400); }));
    scope.querySelectorAll("[data-address-edit]").forEach((button) => button.addEventListener("click", () => { const panel = doc.getElementById(`EditAddress-${button.dataset.addressEdit}`); if (panel) panel.hidden = !panel.hidden; }));
  }

  function init(scope = doc) { initHeader(scope); initPredictiveSearch(scope); initReveal(scope); initQuantity(scope); initProducts(scope); initCart(scope); initCalculator(scope); initFlavours(scope); initVideo(scope); initFilters(scope); initRecipes(scope); initRecommendations(scope); initMisc(scope); }
  init();
  doc.addEventListener("shopify:section:load", (event) => init(event.target));
})();
